/**
 * Element-fullscreen helpers for EmbedHost's toolbar button, with the
 * `webkit`-prefixed fallbacks older Safari (and iPadOS < 16.4) still
 * needs. iPhone Safari supports neither form for non-video elements —
 * `isFullscreenSupported` is false there, and the button is hidden
 * rather than offered and then failing.
 */

interface WebkitDocument {
  fullscreenEnabled?: boolean;
  webkitFullscreenEnabled?: boolean;
  fullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
  exitFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface WebkitElement {
  requestFullscreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void> | void;
}

/** Fullscreen events, standard and prefixed, to subscribe to both. */
export const FULLSCREEN_EVENTS = ["fullscreenchange", "webkitfullscreenchange"] as const;

export const isFullscreenSupported = (doc: unknown): boolean => {
  const d = doc as WebkitDocument | null | undefined;
  return d?.fullscreenEnabled === true || d?.webkitFullscreenEnabled === true;
};

export const fullscreenElement = (doc: unknown): Element | null => {
  const d = doc as WebkitDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
};

export const requestElementFullscreen = async (el: Element): Promise<void> => {
  const e = el as WebkitElement;
  if (typeof e.requestFullscreen === "function") return e.requestFullscreen();
  if (typeof e.webkitRequestFullscreen === "function") {
    await e.webkitRequestFullscreen();
    return;
  }
  throw new Error("fullscreen is not supported");
};

export const exitDocumentFullscreen = async (doc: unknown): Promise<void> => {
  const d = doc as WebkitDocument;
  if (typeof d.exitFullscreen === "function") return d.exitFullscreen();
  if (typeof d.webkitExitFullscreen === "function") await d.webkitExitFullscreen();
};
